/* post-t7-2b (D7): DOS ARREGLOS DEL PANEL "AVANCE DEL GRUPO" EN EL TELEFONO, FUERA DE LA PAGINA.

   ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).

   (1) El boton flotante del chat ("Preguntale al panel") va fijo abajo a la derecha. Al cargar, con la pagina arriba,
   podia quedar encima de un control (medido en Chrome con la foto del demo a 320 por 568: la cabecera "Nunca han
   practicado ... Ver quienes" en ES y "Stopped practicing ... See who" en EN) hasta que se deslizaba la pagina. Ahora,
   si al cargar tapa un control, espera oculto y aparece con el primer deslizamiento; si no tapa nada, se ve desde el
   principio. Ninguna palabra cambia.
   (2) Un nombre de archivo sin espacios ("Finanzas_Corporativas_Unidad_1.pdf") partia a la mitad en "Ver como
   explicarla" a 320. Ahora va en una caja en linea (`inline-block`, a lo mas el ancho del renglon) con puntos de
   corte suaves (`<wbr>`, sin texto) despues de cada guion bajo o diagonal y antes del punto de la extension: si cabe
   entero en un renglon, baja entero como antes (la caja no se parte); si no cabe, parte por esos puntos y sigue sin
   salirse de su caja (el corte a media palabra queda solo como ultimo recurso, el de siempre). Medido en Chrome con el
   demo: a 320, "Finanzas_Corporativas_Unidad_" y "1.pdf" (antes "Finanzas_Corporativas_Unidad_1" y ".pdf", un corte
   que caia donde se acababa el renglon); a 390, entero, como antes. El texto no cambia ni una letra. */
import React, { useEffect, useLayoutEffect, useState } from 'react';

/** Una palabra (sin espacios) de al menos este largo recibe puntos de corte suaves. */
export const LARGO_DE_PALABRA_LARGA = 16;

/** La caja en linea de una palabra larga: baja entera si cabe en un renglon; si no, parte por sus `<wbr>`. */
const CAJA_DE_LA_PALABRA = { display: 'inline-block', maxWidth: '100%' };

/** El texto con cada palabra larga en su caja en linea y con `<wbr>`: despues de `_` o `/`, y antes de un punto
    seguido de letra o numero. Lo que no es texto, o no tiene palabras largas, sale igual. */
export function conCortesSuaves(texto) {
  if (typeof texto !== 'string') return texto;
  const partes = texto.split(/(\s+)/);
  if (!partes.some((p) => p.length >= LARGO_DE_PALABRA_LARGA && !/\s/.test(p))) return texto;
  const salida = [];
  let n = 0;
  for (const p of partes) {
    if (p.length < LARGO_DE_PALABRA_LARGA || /\s/.test(p)) { salida.push(p); continue; }
    const dentro = [];
    let pedazo = '';
    for (let i = 0; i < p.length; i += 1) {
      const c = p[i];
      if (c === '.' && pedazo && /[A-Za-z0-9]/.test(p[i + 1] || '')) {
        dentro.push(pedazo, React.createElement('wbr', { key: `c${n += 1}` }));
        pedazo = '';
      }
      pedazo += c;
      if ((c === '_' || c === '/') && i < p.length - 1) {
        dentro.push(pedazo, React.createElement('wbr', { key: `c${n += 1}` }));
        pedazo = '';
      }
    }
    if (pedazo) dentro.push(pedazo);
    salida.push(dentro.length > 1
      ? React.createElement('span', { key: `p${n += 1}`, 'data-palabra-larga': 'si', style: CAJA_DE_LA_PALABRA }, ...dentro)
      : p);
  }
  return salida;
}

/** `true` si los rectangulos se enciman (mas de medio pixel en los dos ejes). */
export function seEnciman(a, b) {
  return !!(a && b && a.left < b.right - 0.5 && b.left < a.right - 0.5 && a.top < b.bottom - 0.5 && b.top < a.bottom - 0.5);
}

/** El desplazamiento vertical de la animacion de entrada de `el` (0 si no hay): al cargar, la pagina sube desde 16 px
    abajo, y lo que se mide en ese cuadro hay que contarlo donde va a quedar. */
function desplazamientoDeLaEntrada(el) {
  if (!el || typeof window === 'undefined' || !window.getComputedStyle) return 0;
  const m = /matrix\(([^)]+)\)/.exec(window.getComputedStyle(el).transform || '');
  if (!m) return 0;
  const v = m[1].split(',').map((x) => parseFloat(x));
  return v.length === 6 && Number.isFinite(v[5]) ? v[5] : 0;
}

/** Los controles visibles de `raiz` que el boton `flotante` tapa, contados donde quedan al terminar la entrada. No
    cuentan los de dentro del propio flotante ni los ocultos e inertes (los medidores). */
export function controlesTapados(flotante, raiz, pagina) {
  if (!flotante || !raiz) return [];
  const f = flotante.getBoundingClientRect();
  if (!f || !(f.width > 0) || !(f.height > 0)) return [];
  const dy = desplazamientoDeLaEntrada(pagina);
  return Array.from(raiz.querySelectorAll('button, a[href], [role="button"]')).filter((el) => {
    if (flotante.contains(el) || el.closest('[inert]') || el.closest('[aria-hidden="true"]')) return false;
    const r = el.getBoundingClientRect();
    if (!r || !(r.width > 0) || !(r.height > 0)) return false;
    return seEnciman(f, { left: r.left, right: r.right, top: r.top - dy, bottom: r.bottom - dy });
  });
}

/** `true` mientras el boton flotante debe esperar oculto: al cargar tapa un control y la pagina aun no se desliza.
    `activo`: se pinta el flotante; `clave`: cambia con los datos (una recarga vuelve a mirar, salvo que ya se haya
    deslizado). Las refs: el envoltorio del flotante, la raiz del panel y la pagina que entra animada. */
export function useFlotanteQueEspera(activo, clave, flotanteRef, raizRef, paginaSelector) {
  const [espera, setEspera] = useState(false);
  const [deslizado, setDeslizado] = useState(false);
  useLayoutEffect(() => {
    if (!activo || deslizado) { setEspera(false); return; }
    const raiz = raizRef && raizRef.current;
    const pagina = raiz && paginaSelector ? raiz.querySelector(paginaSelector) : null;
    setEspera(controlesTapados(flotanteRef && flotanteRef.current, raiz, pagina).length > 0);
  }, [activo, clave, deslizado, flotanteRef, raizRef, paginaSelector]);
  useEffect(() => {
    if (!espera || typeof window === 'undefined') return undefined;
    // el desplazamiento del panel es el del <main> de la app: su evento no sube, pero pasa por la captura de window
    const alDeslizar = () => setDeslizado(true);
    window.addEventListener('scroll', alDeslizar, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', alDeslizar, { capture: true, passive: true });
  }, [espera]);
  return espera && !deslizado;
}
